import AssetUploadLocation from '@bt-react/libs/AssetUploadLocation'
import { ClosedCaptionsTypes, FeaturesApiResponse } from './closedCaptionsTypes'
import { ServiceError } from '@bt-react/constants'
import axios from 'axios'
import get from 'lodash/get'
import has from 'lodash/has'
import { actions, ResourcesInterface } from '../../../../index';
import { ReduxAction } from '../../../reducers/reducerType'


let BTUploader
if (typeof document !== 'undefined') {
  BTUploader = require('brighttalk-uploader').default
}

export const setCaptionsView = (view: string, state?: boolean) => async (dispatch): Promise<void> => {
  if (state) {
    dispatch({type: ClosedCaptionsTypes.SET_CC_VIEW_ONLY, payload: view})
  } else {
    dispatch({type: ClosedCaptionsTypes.SET_CC_VIEW, payload: view})
  }
}

export const fetchCaptionsDataSuccess = (data: ResourcesInterface, communicationId: string): ReduxAction =>
  ({ type: ClosedCaptionsTypes.FETCH_CAPTIONS_SUCCESS, payload: {data, communicationId} })

export const fetchCaptionsDataError = (error, communicationId): ReduxAction =>
  ({ type: ClosedCaptionsTypes.FETCH_CAPTIONS_FAILED, payload: {error, communicationId} })

export const fetchCaptionsData = (channelId, webcastId, origin = '') => async (dispatch, getState): Promise<void> => {
  dispatch({ type: ClosedCaptionsTypes.FETCH_CAPTIONS, payload: {communicationId: webcastId} })

  const state = getState()
  if (state.closedCaptions && state.closedCaptions.interval && origin !== 'cancel') {
    const interval = state.closedCaptions.interval
    if (interval && interval.length) {
      for (let i = 0; i < interval.length; i++) {
        clearInterval(interval[i])
      }
    }
  }

  try {

    const { data } = await axios.get(`/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaptions`, {
      headers: {
        'BrightTALK-API-Client': state.client
      }
    })
    if (data.closedCaptions) {
      data.closedCaptions.forEach(val=>{
        if (val.file && !val.file.href) {
          if (origin !== 'cancel') {
            dispatch(checkCaptionsUploadStatus(channelId, webcastId, val.id, 'list'))
          } else {
            val.isCancelled = true
          }
        }
      })
    }
    dispatch(fetchCaptionsDataSuccess(data, webcastId))

  }
  catch (e) {
    const status = get(e, 'request.status', 500)
    let errorMessage = {}

    switch (status) {

      case 500:
        errorMessage = {
          code: 500,
          message: ServiceError.NETWORK_ERROR
        }
        break
      default:
        errorMessage = {
          code: e.request.status,
          message: e.message
        }

    }

    dispatch(fetchCaptionsDataError(errorMessage, webcastId))

  }

}

export const fetchChannelFeaturesSuccess = (isEnabled: boolean): ReduxAction =>
  ({ type: ClosedCaptionsTypes.FETCH_CHANNEL_FEATURES_SUCCESS, payload: isEnabled })

export const fetchChannelFeaturesError = (error, channelId: string): ReduxAction =>
  ({ type: ClosedCaptionsTypes.FETCH_CHANNEL_FEATURES_FAILED, payload: {error, channelId} })

export const fetchChannelFeatures = (channelId: string) => async (dispatch, getState): Promise<boolean | undefined> => {
  const { client } = getState()

  try {
    const { data } = await axios.get<FeaturesApiResponse>(`/service/channel/v2/channel/${channelId}/features`, { headers: {
      'BrightTALK-API-Client': client
    }})

    return data.features.find(feat => feat.name === 'autoTransactions')?.enabled

  } catch (e) {

    const status = get(e, 'request.status', 500)
    let errorMessage = {}

    switch (status) {

      case 500:
        errorMessage = {
          code: 500,
          message: ServiceError.NETWORK_ERROR
        }
        break
      default:
        errorMessage = {
          code: e.request.status,
          message: e.message
        }

    }

    dispatch(fetchChannelFeaturesError(errorMessage, channelId))

  }
}

export const create = (data) => async (dispatch, getState): Promise<void> => {

  dispatch({ type: ClosedCaptionsTypes.SUBMITING_CAPTION, payload: data })
  const { client, currentUser } = getState()

  let newAttachment
  let isSuccess = true
  const {channelId, webcastId, id = null} = data
  const postData:any = {
    title: data.language,
    availability: data.availability
  }

  try {
    let response
    if (id) {
      postData.id = id
      response = await axios.put(`/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaption/${postData.id}`, postData, {
        headers: {
          'BrightTALK-API-Client': client
        }
      })
    } else {
      response = await axios.post(`/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaption`, postData, {
        headers: {
          'BrightTALK-API-Client': client
        }
      })
    }
    const { data } = response
    newAttachment = data

  } catch (e) {

    if (has(e, 'response.data')) {

      isSuccess = false
      dispatch({ type: ClosedCaptionsTypes.SUBMIT_CAPTION_ERROR, payload: {error:e.response.data, data: data} })

    }

  }

  if (isSuccess) {

    if (newAttachment) {
      dispatch({type: ClosedCaptionsTypes.SUBMIT_CAPTION_SUCCESS, payload: newAttachment})
      if (!data.file.hosted) {
        await dispatch(submitCaptionForm(newAttachment, data, channelId, webcastId, currentUser.currentUser.userTimezone))
      } else {
        dispatch(fetchCaptionsData(channelId, webcastId))
      }
    } else {

      dispatch({ type: ClosedCaptionsTypes.SUBMIT_CAPTION_ERROR, payload: {code: 'error', message: 'Failed to create'} })

    }

  }
}

export const submitCaptionForm = (attachment, formData, channelId, webcastId, userTimezone) => async (dispatch, getState) => {
  dispatch({ type: ClosedCaptionsTypes.SELECT_CAPTION_UPLOAD })

  const uploader = await BTUploader.initialise({
    'browse_button': document.createElement('div'),
    uploadOnly: true,
    filters: {
      'max_file_size': '1000mb'
    }
  })

  window.onbeforeunload = () => {

    const state = getState()

    return state.closedCaptions.status.isProcessing ? true : undefined

  }

  const attachmentFilePromise = new Promise<void>((resolve, reject) => {
    if (!(formData.file instanceof Blob)) {

      return resolve()

    }

    uploader.on(BTUploader.FILE_UPLOADED, (file) => {

      dispatch({ type: ClosedCaptionsTypes.CAPTION_UPLOAD_SUCCESS })
      dispatch(checkCaptionsUploadStatus(channelId, webcastId, file.id))
      resolve()

    })

    uploader.on(BTUploader.PROGRESS, (file, bytePerSec) => {

      dispatch({ type: ClosedCaptionsTypes.CAPTION_UPLOADING_STATE, payload: {file, bytePerSec}})
      resolve()

    })

    uploader.on(BTUploader.PROCESSING, (file) => {

      dispatch({ type: ClosedCaptionsTypes.CAPTION_PROCESSING_STATE })

    })

    uploader.on(BTUploader.FILE_FINISHED, (file) => {

      dispatch({ type: ClosedCaptionsTypes.CAPTION_PROCESS_SUCCESS })
      dispatch(fetchCaptionsData(channelId, webcastId))

    })

    uploader.on(BTUploader.ERROR, (e) => {

      dispatch({ type: ClosedCaptionsTypes.CAPTION_UPLOAD_FAILED, payload: e })
      resolve()

    })
    try {
      uploader.addFile(formData.file)
    
      const region = AssetUploadLocation(userTimezone)

      uploader.start({
        type: 'resource-fileupload',
        target: {
          id: attachment.id,
          type: 'resource'
        },
        region: region
      })
      dispatch({ type: ClosedCaptionsTypes.ADD_CAPTION_UPLOADER, payload: uploader})
    } catch (e) {
      dispatch({ type: ClosedCaptionsTypes.CAPTION_UPLOAD_FAILED, payload: e })
      resolve()

    }

  })

  const actions = [
    attachmentFilePromise
  ]
  try {

    await Promise.all(actions)

  }
  catch (e) {    
    dispatch({ type: ClosedCaptionsTypes.CAPTION_UPLOAD_FAILED, payload: attachment })
  }

}

export const deleteCaption = (channelId, webcastId, attachmentId, origin='') => async (dispatch, getState): Promise<void> => {

  const { client } = getState()

  if (['client', 'cancel', 'delete'].indexOf(origin) >= 0) dispatch({ type: ClosedCaptionsTypes.DELETE_CAPTION, payload: {communicationId: webcastId} })

  if (origin === 'back') {
    dispatch(fetchCaptionsData(channelId, webcastId))
    return
  }

  const state = getState()

  const uploader = state.closedCaptions.uploader
  if (uploader) {
    uploader.destroy()
  }

  await dispatch(cancelFileUpload(attachmentId))

  if ((!has(state, 'resources.status.isEdit') || origin === 'delete') && origin !== 'processing') {

    try {

      const { data } = await axios.delete(`/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaption/${attachmentId}`, {
        headers: {
          'BrightTALK-API-Client': client
        }
      })
      await dispatch({ type: ClosedCaptionsTypes.DELETE_CAPTION_SUCCESS })
      dispatch({ type: ClosedCaptionsTypes.SET_CC_VIEW, payload: 'lists' })
      dispatch(fetchCaptionsData(channelId, webcastId))

    } catch (e) {
      const status = get(e, 'request.status', 500)
      let errorMessage = {}

      switch (status) {

        case 500:
          errorMessage = {
            code: 500,
            message: ServiceError.NETWORK_ERROR
          }
          break
        default:
          errorMessage = {
            code: e.request.status,
            message: e.message
          }

      }

      dispatch({ type: ClosedCaptionsTypes.DELETE_CAPTION_FAILED, payload: errorMessage })
    }

  } else {
    if (origin !== 'processing') {
      dispatch({ type: ClosedCaptionsTypes.SET_CC_VIEW, payload: 'lists' })
      if (origin === 'cancel') dispatch(fetchCaptionsData(channelId, webcastId, 'cancel'))
    }
  }

}

export const cancelFileUpload = (resourceId) => async (dispatch): Promise<void> => {

  try {
    const url = `/service/ams/resource/${resourceId}/settings/fileupload`
    const res = await dispatch(getUploadedImage(url))

    if (res.data.asset) {
      try {
    
        const response = await axios.put(`/service/ams/asset/${res.data.asset.id}`, {id: resourceId, status: {state: 'cancelled'}})
      
      } catch (e) {
        const status = get(e, 'request.status', 500)
        let errorMessage = {}

        switch (status) {

          case 500:
            errorMessage = {
              code: 500,
              message: ServiceError.NETWORK_ERROR
            }
            break
          default:
            errorMessage = {
              code: e.request.status,
              message: e.message
            }

        }
      
      }
    }
  } catch (e) {

  }
      
}

export const getUploadedImage = (url) => async (dispatch, getState) => {

  const { client } = getState()
  try {

    const res = await axios.get(url, {
      headers: {
        Accept: 'application/json',
        'BrightTALK-API-Client': client
      }
    })

    return res
  } catch(e) {
    throw new Error(e.message)
  }

}

export const checkCaptionsUploadStatus = (channelId, webcastId, resourceId, state = '') => async (dispatch): Promise<void> => {
  let processingState = false
  let assetId = resourceId
  if (state === 'list') {
    try {
      const url = `/service/ams/resource/${resourceId}/settings/fileupload`
      const res = await dispatch(getUploadedImage(url))
      if (res.data.asset) {
        if (['complete', 'uploading', 'processingfailed', 'cancelled'].includes(res.data.asset.status.state)) {
          dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.asset.status.state))
          dispatch(fetchCaptionsData(channelId, webcastId, 'cancel'))
        }  else {
          processingState = true
          assetId = res.data.asset.id
          dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.asset.status.state))
        }
      } else {
        dispatch({ type: ClosedCaptionsTypes.CAPTION_NOT_FOUND, payload: { resourceId } })
        dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.asset.status.state))
      }
    } catch (e) {
      dispatch(updateCancelState(channelId, webcastId, resourceId))
    }
  }

  if (state!=='list' || processingState) {

    const attachmentProcessingTimer = setInterval(async () => {

      try {
        const url = `/service/ams/asset/${assetId}`
        const res = await dispatch(getUploadedImage(url))
        if (res.data.id) {
          if (['complete', 'cancelled'].includes(res.data.status.state)) {

            clearInterval(attachmentProcessingTimer)
            if (state === 'list') {

              dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.status.state))
              dispatch(fetchCaptionsData(channelId, webcastId))
            } else if (res.data.status.state !== 'cancelled') {
              dispatch({ type: ClosedCaptionsTypes.CAPTION_PROCESS_SUCCESS })
              dispatch(fetchCaptionsData(channelId, webcastId))
            }

          } else {
            if (state === 'list') {

              dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.status.state))
            } else if (res.data.status.state === 'processingfailed') {
              dispatch({ type: ClosedCaptionsTypes.CAPTION_PROCESSING_FAILED, payload: {error: 'processing failed'} })
            }
          }

        } else {

          dispatch({ type: ClosedCaptionsTypes.CAPTION_NOT_FOUND, payload: { resourceId } })
          clearInterval(attachmentProcessingTimer)

          if (state === 'list') dispatch(updateCancelState(channelId, webcastId, resourceId, res.data.status.state))

        }
      } catch (e) {

        clearInterval(attachmentProcessingTimer)
        if (state === 'list') dispatch(updateCancelState(channelId, webcastId, resourceId))
      }

    }, 5000)
    dispatch({ type: ClosedCaptionsTypes.SAVE_CAPTION_STATUS_INTERVAL, payload: attachmentProcessingTimer})
    if (state !== 'list') {
      dispatch({ type: ClosedCaptionsTypes.CAPTION_PROCESSING_STATE })
    }

  }

}

export const selectCaption = (captionData) => async (dispatch): Promise<void> => {
  dispatch({type: ClosedCaptionsTypes.SELECT_CAPTION, payload: captionData})
}

export const updateCancelState = (channelId, webcastId, resourceId, captionState = null) => async (dispatch, getState) => {
  try {
  const state = getState()
  if (state.closedCaptions && webcastId && state.closedCaptions[webcastId] && state.closedCaptions[webcastId].data && state.closedCaptions[webcastId].data.closedCaptions) {
    const index = get(state.closedCaptions[webcastId].data, 'closedCaptions').findIndex((val)=> val.id === resourceId)
    if (index !== -1) {
        const url = `/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaption/${resourceId}`
        const res = await dispatch(getUploadedImage(url))
        state.closedCaptions[webcastId].data.closedCaptions[index] = {...res.data, state: captionState}
        const captions = state.closedCaptions[webcastId]
        dispatch({ type: ClosedCaptionsTypes.UPDATE_CAPTION_CANCELLED_STATE, payload: {captions, webcastId} })
    }

  }
  } catch (e) {
    throw new Error(e.message)
  }

}

export const rearrangeCaptions = (channelId: string, webcastId: string, captionsData: any) => async (dispatch, getState): Promise<void> => {

  const { client } = getState()
  dispatch({ type: ClosedCaptionsTypes.REARRANGE_CAPTIONS, payload: {communicationId: webcastId} })

  try {

    const { data } = await axios.put(`/service/communication/v1/channel/${channelId}/communication/${webcastId}/closedcaptions`, {closedCaptions: captionsData}, {
      headers: {
        'BrightTALK-API-Client': client
      }
    })
    await dispatch({ type: ClosedCaptionsTypes.REARRANGE_CAPTIONS_SUCCESS, payload: {data} })
    dispatch({ type: ClosedCaptionsTypes.REARRANGE_CAPTIONS_SUCCESS, payload: {data} })

  } catch (e) {
    const status = get(e, 'request.status', 500)
    let errorMessage = {}

    switch (status) {

      case 500:
        errorMessage = {
          code: 500,
          message: ServiceError.NETWORK_ERROR
        }
        break
      default:
        errorMessage = {
          code: e.request.status,
          message: e.message
        }

    }

    dispatch({ type: ClosedCaptionsTypes.REARRANGE_CAPTIONS_FAILURE, payload: errorMessage })

  }
}